/**
 * ClientPolarisVizProvider Component
 *
 * Client-side wrapper that loads PolarisVizProvider dynamically.
 * Prevents SSR from importing polaris-viz which crashes due to useLayoutEffect.
 *
 * This component includes robust Firefox compatibility by verifying stylesheets
 * are fully loaded before importing polaris-viz, which internally accesses
 * stylesheet.cssRules and throws if the sheets aren't ready.
 */

import { useState, useEffect, type ReactNode } from 'react';

type PolarisVizProviderType = React.ComponentType<{ children: ReactNode }>;

/**
 * Check if all stylesheets are loaded and accessible
 * Returns true when it's safe to import polaris-viz
 */
function areStylesheetsReady(): boolean {
  try {
    const stylesheets = Array.from(document.styleSheets);
    return stylesheets.every((sheet) => {
      try {
        // Accessing cssRules throws in Firefox if stylesheet is not ready
        // or throws SecurityError for cross-origin sheets (which is fine)
        // We just need to verify we can access it without InvalidAccessError
        const rules = sheet.cssRules;
        return rules !== null || true;
      } catch (e) {
        // SecurityError for cross-origin sheets is acceptable
        // InvalidAccessError in Firefox means sheet not ready
        if (e instanceof DOMException) {
          return e.name === 'SecurityError';
        }
        return false;
      }
    });
  } catch {
    // If we can't even enumerate stylesheets, assume not ready
    return false;
  }
}

interface ClientPolarisVizProviderProps {
  children: ReactNode;
}

export function ClientPolarisVizProvider({ children }: ClientPolarisVizProviderProps) {
  const [Provider, setProvider] = useState<PolarisVizProviderType | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadProvider = () => {
      import('@shopify/polaris-viz').then((mod) => {
        if (isMounted) {
          setProvider(() => mod.PolarisVizProvider);
        }
      });
    };

    // Firefox fix: Verify stylesheets are fully parsed before loading polaris-viz
    // polaris-viz accesses stylesheet.cssRules which throws if sheets aren't ready
    // Add timeout to prevent infinite waiting if stylesheets never load
    const STYLESHEET_TIMEOUT_MS = 5000;
    const startTime = Date.now();

    const waitForStylesheets = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= STYLESHEET_TIMEOUT_MS) {
        // Timeout reached - log warning and load provider anyway
        console.warn(
          '[ClientPolarisVizProvider] Stylesheet wait timeout after 5s, loading provider anyway'
        );
        if (isMounted) loadProvider();
        return;
      }

      if (areStylesheetsReady()) {
        // Double-RAF ensures we're after the browser's style recalculation
        // First RAF waits for next paint, second RAF ensures styles are applied
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (isMounted) {
              loadProvider();
            }
          });
        });
      } else {
        // Retry in next frame - stylesheets still loading
        requestAnimationFrame(() => {
          if (isMounted) {
            waitForStylesheets();
          }
        });
      }
    };

    if (document.readyState === 'complete') {
      waitForStylesheets();
    } else {
      const handleLoad = () => waitForStylesheets();
      window.addEventListener('load', handleLoad, { once: true });
      return () => {
        isMounted = false;
        window.removeEventListener('load', handleLoad);
      };
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // On SSR or before client loads, render children without the provider
  // Charts will show loading state until provider is ready
  if (!Provider) {
    return <>{children}</>;
  }

  return <Provider>{children}</Provider>;
}

export default ClientPolarisVizProvider;
